import { useEffect, useState, useRef } from "react";

import { useParams, useNavigate } from "react-router-dom";

import { api, formatApiError } from "@/lib/api";

import { Card } from "@/components/ui/card";

import { Button } from "@/components/ui/button";

import { Input } from "@/components/ui/input";

import { Textarea } from "@/components/ui/textarea";

import { Badge } from "@/components/ui/badge";

import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

import { Checkbox } from "@/components/ui/checkbox";

import { Label } from "@/components/ui/label";

import StatusBadge from "@/components/StatusBadge";

import SignaturePad from "@/components/SignaturePad";

import { Check, X, Camera, ChevronLeft, Truck, ImageIcon, Send, Clock, MapPin, PenLine, AlertCircle, Star, Hash } from "lucide-react";

import { toast } from "sonner";



const fmtDT = (d) => new Date(d).toLocaleString("pt-BR");



const NEGATIVE_OPTIONS = ["defeito", "não", "nao", "atenção", "atencao", "avaria", "reprovado", "critico", "crítico"];



function isNeg(v) { return typeof v === "string" && NEGATIVE_OPTIONS.includes(v.toLowerCase()); }



/**

 * Compress an image file via HTML5 Canvas:

 * - Resizes so largest dimension is <= 1024px (preserving aspect ratio)

 * - Re-encodes as JPEG quality 0.6 (\~100-200KB per photo)

 * - Returns base64 dataURL suitable for storing in state

 */

async function compressImage(file, maxDim = 1024, quality = 0.6) {

  return new Promise((resolve, reject) => {

    if (!file) return resolve(null);

    const reader = new FileReader();

    reader.onerror = () => reject(new Error("Falha ao ler arquivo"));

    reader.onload = () => {

      const img = new Image();

      img.onerror = () => reject(new Error("Falha ao carregar imagem"));

      img.onload = () => {

        try {

          let { width, height } = img;

          if (width > maxDim || height > maxDim) {

            const scale = maxDim / Math.max(width, height);

            width = Math.round(width * scale);

            height = Math.round(height * scale);

          }

          const canvas = document.createElement("canvas");

          canvas.width = width; canvas.height = height;

          const ctx = canvas.getContext("2d");

          // White background so transparent PNGs don't turn black in JPEG

          ctx.fillStyle = "#FFFFFF";

          ctx.fillRect(0, 0, width, height);

          ctx.drawImage(img, 0, 0, width, height);

          resolve(canvas.toDataURL("image/jpeg", quality));

        } catch (err) { reject(err); }

      };

      img.src = reader.result;

    };

    reader.readAsDataURL(file);

  });

}





/** Returns per-item entries requiring photo+notes when the answer has negative responses. */

function negativeItems(q, value) {

  const out = [];

  if (!value) return out;

  if (q.type === "option_table" && typeof value === "object") {

    (q.rows || []).forEach((row, ri) => {

      const r = value[ri];

      if (isNeg(r)) out.push({ key: String(ri), label: row, response: r });

    });

    return out;

  }

  if (q.type === "multi_choice" && Array.isArray(value)) {

    value.forEach((c) => { if (isNeg(c)) out.push({ key: c, label: c, response: c }); });

    return out;

  }

  if (q.type === "single_choice" && isNeg(value)) {

    out.push({ key: "_self", label: q.label, response: value });

  }

  return out;

}



export default function ChecklistFiller() {

  const { taskId } = useParams();

  const nav = useNavigate();

  const [task, setTask] = useState(null);

  const [answers, setAnswers] = useState({});

  const [notes, setNotes] = useState("");

  const [signature, setSignature] = useState("");

  const [submitting, setSubmitting] = useState(false);

  const [startedAt, setStartedAt] = useState(() => new Date().toISOString());

  const [geo, setGeo] = useState({ status: "idle", lat: null, lng: null, error: null });

  const [collaborators, setCollaborators] = useState([]);

  const [vehicles, setVehicles] = useState([]);

  const fileRefs = useRef({});



  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const [{ data: taskData }, { data: logsData }] = await Promise.all([
          api.get(`/tasks/${taskId}`),
          api.get(`/tasks/${taskId}/logs`),
        ]);

        if (cancelled) return;

        setTask(taskData);

        const latestLog = Array.isArray(logsData) ? logsData[0] : null;

        if (latestLog) {
          const restoredAnswers = {};

          for (const row of latestLog.answers_json || []) {
            if (!row?.question_id) continue;

            let restoredValue = row.value;

            if (row.type === "option_table" && Array.isArray(restoredValue)) {
              const tableValue = {};

              restoredValue.forEach((pair, index) => {
                if (pair && typeof pair === "object") {
                  tableValue[index] = pair.response ?? "";
                }
              });

              restoredValue = tableValue;
            }

            const restoredDefects = {};

            for (const defect of row.item_defects || []) {
              const key = String(defect.key ?? "");
              if (!key) continue;

              restoredDefects[key] = {
                photo: defect.photo || "",
                notes: defect.notes || "",
              };
            }

            restoredAnswers[row.question_id] = {
              value: restoredValue ?? "",
              photo: row.photo || "",
              signature:
                row.signature ||
                (row.type === "signature" ? row.value || "" : ""),
              defects: restoredDefects,
            };
          }

          setAnswers(restoredAnswers);
          setNotes(latestLog.notes || "");
          setSignature(latestLog.signature || "");

          if (taskData.status === "Pendente Correção") {
            setStartedAt(new Date().toISOString());
          } else if (latestLog.started_at) {
            setStartedAt(latestLog.started_at);
          }
        }
      } catch (error) {
        if (!cancelled) {
          toast.error(
            formatApiError(
              error.response?.data?.detail ||
                "Não foi possível carregar o checklist."
            )
          );
        }
      }
    };

    load();

    api
      .get("/collaborators?active_only=false")
      .then(({ data }) => {
        if (!cancelled) setCollaborators(data || []);
      })
      .catch(() => {});

    api
      .get("/vehicles")
      .then(({ data }) => {
        if (!cancelled) setVehicles(data || []);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [taskId]);

  useEffect(() => {

    if (!navigator.geolocation) { setGeo({ status: "unsupported", lat: null, lng: null, error: "GPS não suportado" }); return; }

    setGeo((g) => ({ ...g, status: "requesting" }));

    navigator.geolocation.getCurrentPosition(

      (p) => setGeo({ status: "ok", lat: p.coords.latitude, lng: p.coords.longitude, error: null }),

      (err) => setGeo({ status: "denied", lat: null, lng: null, error: err.message }),

      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }

    );

  }, []);



  const setA = (qid, patch) => setAnswers((prev) => ({ ...prev, [qid]: { ...(prev[qid] || {}), ...patch } }));



  const onPhoto = (qid, file, key = "photo") => {

    if (!file) return;

    compressImage(file).then((dataUrl) => {

      if (dataUrl) setA(qid, { [key]: dataUrl });

    }).catch((err) => {

      console.error("Falha ao comprimir foto", err);

      // Fallback: original file via FileReader

      const reader = new FileReader();

      reader.onload = () => setA(qid, { [key]: reader.result });

      reader.readAsDataURL(file);

    });

  };



  const submit = async () => {

    if (!task) return;

    const sections = task.template?.structure_json || [];

    const rows = [];

    let missing = null;

    // Helper: shouldShow for validation (same rules as UI)

    const evalShow = (q) => {

      const cond = q.show_if;

      if (!cond || !cond.other_id) return true;

      const other = answers[cond.other_id];

      const val = other?.value;

      if (!val) return false;

      const match = String(cond.match || "").toLowerCase();

      if (Array.isArray(val)) return val.some((v) => String(v).toLowerCase().includes(match));

      return String(val).toLowerCase().includes(match);

    };

    for (const s of sections) {

      for (const q of s.questions || []) {

        if (q.type === "header" || q.type === "paragraph") continue;

        if (!evalShow(q)) continue;

        const a = answers[q.id] || {};

        if (q.type === "pass_fail" && !a.value) { missing = q.label; break; }

        if (q.type === "numeric" && (a.value === undefined || a.value === "")) { missing = q.label; break; }

        if (q.type === "single_choice" && !a.value) { missing = q.label; break; }

        if (q.type === "autocomplete" && !a.value) { missing = q.label; break; }

        if (q.type === "collaborator_select" && !a.value) { missing = q.label; break; }

        if (q.type === "vehicle_select" && !a.value) { missing = q.label; break; }

        if (q.type === "rating" && !a.value) { missing = q.label; break; }

        if (q.type === "multi_choice" && (!a.value || a.value.length === 0)) { missing = q.label; break; }

        if (q.type === "option_table" && (!a.value || Object.keys(a.value).length < (q.rows?.length || 0))) { missing = q.label; break; }

        if (q.type === "photo" && !a.photo) { missing = `Foto: ${q.label}`; break; }

        if (q.type === "signature" && !a.signature) { missing = `Assinatura: ${q.label}`; break; }

        if (q.photo_required && !a.photo) { missing = `Foto obrigatória: ${q.label}`; break; }

        // Per-item defect validation

        const negItems = negativeItems(q, a.value);

        for (const it of negItems) {

          const d = (a.defects || {})[it.key] || {};

          if (!d.photo) { missing = `Foto obrigatória em "${it.label}"`; break; }

          if (!d.notes) { missing = `Justificativa obrigatória em "${it.label}"`; break; }

        }

        if (missing) break;

        // Serialize option_table value into array of {item, response, defect_photo?, defect_notes?}

        let submittedValue = a.value ?? "";

        // Fotos e assinaturas são armazenadas em campos próprios para
        // não virarem texto ou anexos genéricos no PDF.
        if (q.type === "photo" || q.type === "signature") {
          submittedValue = "";
        }

        if (q.type === "option_table" && a.value && typeof a.value === "object") {

          submittedValue = (q.rows || []).map((rowLabel, ri) => {

            const resp = a.value[ri] || "N/A";

            const d = (a.defects || {})[String(ri)] || {};

            const pair = { item: rowLabel, response: resp };

            if (d.photo) pair.defect_photo = d.photo;

            if (d.notes) pair.defect_notes = d.notes;

            return pair;

          });

        }

        // Build item_defects for approvals/PDF rendering

        const itemDefects = negItems.map((it) => {

          const d = (a.defects || {})[it.key] || {};

          return { key: it.key, label: it.label, response: it.response, photo: d.photo || "", notes: d.notes || "" };

        });

        rows.push({
          question_id: q.id,
          question: q.label,
          type: q.type,
          value: submittedValue,
          has_photo: !!a.photo,
          photo: a.photo || "",
          signature: q.type === "signature" ? a.signature || "" : "",
          item_defects: itemDefects,
        });

      }

      if (missing) break;

    }

    if (missing) { toast.error(`Preencha: ${missing}`); return; }

    if (!signature) { toast.error("Assinatura do inspetor é obrigatória"); return; }



    const photos = Object.values(answers)
      .map((a) => a.photo)
      .filter(Boolean);

    // Inclui somente fotos de defeitos. Assinaturas ficam fora de `photos`
    // e são salvas nos campos próprios para aparecerem só em Assinaturas.
    Object.values(answers).forEach((a) => {
      Object.values(a.defects || {}).forEach((d) => {
        if (d.photo) photos.push(d.photo);
      });
    });



    setSubmitting(true);

    try {

      await api.post(`/tasks/${taskId}/submit`, {

        answers_json: rows, photos, notes, signature,

        latitude: geo.lat, longitude: geo.lng, started_at: startedAt,

      });

      toast.success("Inspeção enviada!");

      nav("/inbox");

    } catch (e) {

      toast.error(formatApiError(e.response?.data?.detail));

    } finally { setSubmitting(false); }

  };



  if (!task) return <div className="p-10 text-center text-muted-foreground">Carregando...</div>;

  const readOnly = task.status !== "Pendente" && task.status !== "Em Andamento" && task.status !== "Pendente Correção";

  const sections = task.template?.structure_json || [];



  // show_if evaluator: hide questions whose show_if condition doesn't match another answer.

  const shouldShow = (q) => {

    const cond = q.show_if;

    if (!cond || !cond.other_id) return true;

    const other = answers[cond.other_id];

    const val = other?.value;

    if (!val) return false;

    const match = String(cond.match || "").toLowerCase();

    if (Array.isArray(val)) return val.some((v) => String(v).toLowerCase().includes(match));

    return String(val).toLowerCase().includes(match);

  };



  return (

    <div className="pb-28" data-testid="filler-page">

      <div className="sticky top-0 lg:top-0 z-30 bg-card border-b border-border p-4">

        <div className="max-w-3xl mx-auto flex items-center gap-3">

          <Button variant="ghost" size="icon" onClick={() => nav("/inbox")} data-testid="back-btn"><ChevronLeft className="w-5 h-5" /></Button>

          {task.template?.left_logo && <img src={task.template.left_logo} alt="logo" className="h-8 max-w-[80px] object-contain rounded-sm bg-white/80 p-0.5" />}

          <div className="flex-1 min-w-0">

            <div className="font-display font-extrabold text-lg leading-tight truncate">{task.template?.title}</div>

            {task.vehicle ? (

              <div className="text-xs text-muted-foreground flex items-center gap-1.5 font-mono">

                <Truck className="w-3 h-3" /> #{task.vehicle.fleet_number} · {task.vehicle.plate} · {task.vehicle.model}

              </div>

            ) : (

              <div className="text-xs text-muted-foreground italic">Preenchimento manual</div>

            )}

            {task.protocol_number && (

              <div className="text-[10px] uppercase tracking-widest text-primary font-mono mt-0.5" data-testid="protocol-number">

                <Hash className="w-2.5 h-2.5 inline" /> {task.protocol_number}

              </div>

            )}

          </div>

          {task.template?.right_logo && <img src={task.template.right_logo} alt="logo" className="h-8 max-w-[80px] object-contain rounded-sm bg-white/80 p-0.5" />}

          <StatusBadge status={task.status} />

        </div>

      </div>



      <div className="max-w-3xl mx-auto p-4 space-y-4">

        {task.status === "Pendente Correção" && task.reject_reason && (

          <Card className="p-4 border-red-500/50 bg-red-500/5" data-testid="reject-banner">

            <div className="flex items-start gap-2">

              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />

              <div className="flex-1 min-w-0">

                <div className="text-sm font-display font-bold uppercase tracking-tight text-red-600 dark:text-red-400">Correção Solicitada</div>

                <div className="text-sm mt-1 text-foreground">{task.reject_reason}</div>

                <div className="text-xs mt-2 text-muted-foreground">Ajuste os campos indicados e reenvie a inspeção.</div>

              </div>

            </div>

          </Card>

        )}

        {/* Metadata card */}

        <Card className="p-4">

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">

            <div className="flex items-start gap-2">

              <Clock className="w-4 h-4 text-primary mt-0.5" />

              <div>

                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Início</div>

                <div className="font-mono text-xs" data-testid="meta-started-at">{fmtDT(startedAt)}</div>

              </div>

            </div>

            <div className="flex items-start gap-2">

              <MapPin className={`w-4 h-4 mt-0.5 ${geo.status === "ok" ? "text-emerald-500" : "text-amber-500"}`} />

              <div>

                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">GPS</div>

                <div className="font-mono text-xs" data-testid="meta-geo">

                  {geo.status === "ok" && `${geo.lat.toFixed(5)}, ${geo.lng.toFixed(5)}`}

                  {geo.status === "requesting" && "Obtendo localização..."}

                  {geo.status === "denied" && "Permissão negada"}

                  {geo.status === "unsupported" && "Não suportado"}

                  {geo.status === "idle" && "—"}

                </div>

              </div>

            </div>

          </div>

        </Card>



        {task.trigger_meta?.alert_type && (

          <Card className="p-3 bg-primary/5 border-primary/30">

            <div className="text-xs uppercase tracking-widest text-primary font-semibold">Gatilho Fleetrun</div>

            <div className="text-sm mt-1"><span className="font-mono">{task.trigger_meta.alert_type}</span> · {task.trigger_meta.message}</div>

          </Card>

        )}



        {sections.map((s, si) => (

          <Card key={si} className="p-4 sm:p-5" data-testid={`filler-section-${si}`}>

            <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-1">Seção {si + 1}</div>

            <h3 className="font-display font-bold text-xl mb-4">{s.section}</h3>

            <div className="space-y-4">

              {(s.questions || []).map((q) => {

                const a = answers[q.id] || {};



                // Conditional question — hidden until the referenced answer matches

                if (!shouldShow(q)) return null;



                // Display-only components

                if (q.type === "header") {

                  return (

                    <div key={q.id} className="pt-4 border-t border-border" data-testid={`q-${q.id}`}>

                      <h4 className="font-display font-bold text-lg tracking-tight text-primary">{q.label}</h4>

                    </div>

                  );

                }

                if (q.type === "paragraph") {

                  return (

                    <div key={q.id} className="pt-4 border-t border-border" data-testid={`q-${q.id}`}>

                      <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{q.label}</p>

                    </div>

                  );

                }



                return (

                  <div key={q.id} className="border-t border-border pt-4" data-testid={`q-${q.id}`}>

                    <div className="flex items-start justify-between gap-2 mb-3">

                      <div className="text-sm sm:text-base font-medium">{q.label}</div>

                      {q.photo_required && <Badge variant="outline" className="text-[10px]">Foto obrigatória</Badge>}

                    </div>



                    {q.type === "pass_fail" && (

                      <div className="grid grid-cols-2 gap-2">

                        <button type="button" disabled={readOnly} data-testid={`ok-${q.id}`}

                          onClick={() => setA(q.id, { value: "ok" })}

                          className={`h-14 rounded-md border-2 font-display font-bold uppercase tracking-tight flex items-center justify-center gap-2 transition-all active:scale-95 ${

                            a.value === "ok" ? "bg-emerald-500 text-white border-emerald-500 shadow-lg shadow-emerald-500/30" : "border-border hover:border-emerald-500/60"

                          }`}>

                          <Check className="w-5 h-5" /> Conforme

                        </button>

                        <button type="button" disabled={readOnly} data-testid={`defect-${q.id}`}

                          onClick={() => setA(q.id, { value: "defect" })}

                          className={`h-14 rounded-md border-2 font-display font-bold uppercase tracking-tight flex items-center justify-center gap-2 transition-all active:scale-95 ${

                            a.value === "defect" ? "bg-red-500 text-white border-red-500 shadow-lg shadow-red-500/30" : "border-border hover:border-red-500/60"

                          }`}>

                          <X className="w-5 h-5" /> Defeito

                        </button>

                      </div>

                    )}



                    {q.type === "single_choice" && (

                      <RadioGroup value={a.value || ""} onValueChange={(v) => setA(q.id, { value: v })} disabled={readOnly} className="space-y-2">

                        {(q.choices || []).map((c, ci) => (

                          <div key={ci} className="flex items-center space-x-2 p-2 rounded-md border border-border hover:bg-secondary">

                            <RadioGroupItem value={c} id={`${q.id}-${ci}`} data-testid={`radio-${q.id}-${ci}`} />

                            <Label htmlFor={`${q.id}-${ci}`} className="cursor-pointer flex-1">{c}</Label>

                          </div>

                        ))}

                      </RadioGroup>

                    )}



                    {q.type === "multi_choice" && (

                      <div className="space-y-2">

                        {(q.choices || []).map((c, ci) => {

                          const selected = (a.value || []).includes(c);

                          return (

                            <div key={ci} className="flex items-center space-x-2 p-2 rounded-md border border-border hover:bg-secondary">

                              <Checkbox id={`${q.id}-${ci}`} checked={selected} disabled={readOnly}

                                onCheckedChange={(v) => {

                                  const cur = a.value || [];

                                  setA(q.id, { value: v ? [...cur, c] : cur.filter((x) => x !== c) });

                                }}

                                data-testid={`check-${q.id}-${ci}`} />

                              <Label htmlFor={`${q.id}-${ci}`} className="cursor-pointer flex-1">{c}</Label>

                            </div>

                          );

                        })}

                      </div>

                    )}



                    {q.type === "autocomplete" && (

                      <>

                        <Input list={`auto-${q.id}`} value={a.value ?? ""} disabled={readOnly}

                          onChange={(e) => setA(q.id, { value: e.target.value })}

                          placeholder="Digite ou selecione..." className="h-12 text-base"

                          data-testid={`auto-${q.id}`} />

                        <datalist id={`auto-${q.id}`}>

                          {(q.choices || []).map((c, ci) => <option key={ci} value={c} />)}

                        </datalist>

                      </>

                    )}



                    {q.type === "collaborator_select" && (

                      <select value={a.value || ""} disabled={readOnly}

                        onChange={(e) => setA(q.id, { value: e.target.value })}

                        className="w-full h-12 text-base rounded-md border border-input bg-background px-3"

                        data-testid={`collab-${q.id}`}>

                        <option value="">Selecione um colaborador...</option>

                        {collaborators.map((c) => (

                          <option key={c.id} value={c.id}>{c.name} · {c.role}{c.document ? ` · ${c.document}` : ""}</option>

                        ))}

                      </select>

                    )}



                    {q.type === "vehicle_select" && (

                      <select value={a.value || ""} disabled={readOnly}

                        onChange={(e) => setA(q.id, { value: e.target.value })}

                        className="w-full h-12 text-base rounded-md border border-input bg-background px-3"

                        data-testid={`vehsel-${q.id}`}>

                        <option value="">Selecione um veículo...</option>

                        {vehicles.map((v) => (

                          <option key={v.id} value={v.id}>#{v.fleet_number} · {v.plate} · {v.marca ? `${v.marca} ` : ""}{v.model}</option>

                        ))}

                      </select>

                    )}



                    {q.type === "rating" && (

                      <div className="flex items-center gap-1" data-testid={`rating-${q.id}`}>

                        {Array.from({ length: q.max || 5 }, (_, i) => i + 1).map((n) => (

                          <button key={n} type="button" disabled={readOnly}

                            onClick={() => setA(q.id, { value: n })}

                            className="active:scale-90 transition-transform"

                            data-testid={`star-${q.id}-${n}`}>

                            <Star className={`w-9 h-9 ${(a.value || 0) >= n ? "fill-primary text-primary" : "text-muted-foreground"}`} />

                          </button>

                        ))}

                        {a.value ? <span className="ml-2 font-display font-bold text-lg">{a.value}/{q.max || 5}</span> : null}

                      </div>

                    )}



                    {q.type === "option_table" && (

                      <div className="overflow-x-auto">

                        <table className="w-full text-sm border border-border rounded-md" data-testid={`table-${q.id}`}>

                          <thead className="bg-secondary">

                            <tr>

                              <th className="p-2 text-left font-semibold text-xs uppercase tracking-widest text-muted-foreground">Item</th>

                              {(q.columns || []).map((c, ci) => <th key={ci} className="p-2 text-center font-semibold text-xs uppercase tracking-widest text-muted-foreground">{c}</th>)}

                            </tr>

                          </thead>

                          <tbody>

                            {(q.rows || []).map((r, ri) => (

                              <tr key={ri} className="border-t border-border">

                                <td className="p-2 font-medium">{r}</td>

                                {(q.columns || []).map((c, ci) => {

                                  const table = a.value || {};

                                  const checked = table[ri] === c;

                                  return (

                                    <td key={ci} className="p-2 text-center">

                                      <input type="radio" name={`${q.id}-${ri}`} disabled={readOnly}

                                        checked={checked}

                                        onChange={() => setA(q.id, { value: { ...table, [ri]: c } })}

                                        className="w-5 h-5 accent-primary"

                                        data-testid={`opt-${q.id}-${ri}-${ci}`} />

                                    </td>

                                  );

                                })}

                              </tr>

                            ))}

                          </tbody>

                        </table>

                      </div>

                    )}



                    {q.type === "numeric" && (

                      <Input type="number" placeholder="Informe o valor" disabled={readOnly}

                        value={a.value ?? ""} onChange={(e) => setA(q.id, { value: e.target.value })}

                        className="h-12 text-base" data-testid={`numeric-${q.id}`} />

                    )}



                    {q.type === "text" && (

                      <Textarea placeholder={q.placeholder || "Observações"} disabled={readOnly}

                        value={a.value ?? ""} onChange={(e) => setA(q.id, { value: e.target.value })}

                        rows={q.rows || 3} data-testid={`text-${q.id}`} />

                    )}



                    {q.type === "photo" && (

                      <div>

                        <input ref={(r) => (fileRefs.current[q.id] = r)} type="file" accept="image/*" capture="environment"

                          className="hidden" onChange={(e) => onPhoto(q.id, e.target.files?.[0])} data-testid={`photo-input-${q.id}`} />

                        <div className="flex items-center gap-3">

                          <Button type="button" variant="outline" disabled={readOnly}

                            onClick={() => fileRefs.current[q.id]?.click()}

                            className="gap-2 h-12" data-testid={`photo-btn-${q.id}`}>

                            <Camera className="w-4 h-4" /> {a.photo ? "Trocar foto" : "Capturar foto obrigatória"}

                          </Button>

                          {a.photo && <img src={a.photo} alt="preview" className="w-16 h-16 object-cover rounded-md border border-border" />}

                        </div>

                      </div>

                    )}



                    {q.type === "signature" && (

                      <div>

                        <div className="flex items-center gap-1 mb-2 text-xs text-muted-foreground"><PenLine className="w-3 h-3" />Assinatura no bloco</div>

                        <SignaturePad value={a.signature} onChange={(v) => setA(q.id, { signature: v })} disabled={readOnly} />

                      </div>

                    )}



                    {q.type !== "photo" && q.type !== "signature" && q.type !== "header" && q.type !== "paragraph" && (q.photo_required || a.value === "defect") && (

                      <div className="mt-3">

                        <input ref={(r) => (fileRefs.current[q.id] = r)} type="file" accept="image/*" capture="environment"

                          className="hidden" onChange={(e) => onPhoto(q.id, e.target.files?.[0])} data-testid={`photo-input-${q.id}`} />

                        <div className="flex items-center gap-3">

                          <Button type="button" variant="outline" size="sm" disabled={readOnly}

                            onClick={() => fileRefs.current[q.id]?.click()}

                            className="gap-2" data-testid={`photo-btn-${q.id}`}>

                            <Camera className="w-4 h-4" /> {a.photo ? "Trocar foto" : "Capturar foto"}

                          </Button>

                          {a.photo && (

                            <img src={a.photo} alt="preview" className="w-16 h-16 object-cover rounded-md border border-border" />

                          )}

                          {!a.photo && <div className="text-xs text-muted-foreground flex items-center gap-1"><ImageIcon className="w-3.5 h-3.5" /> Nenhuma foto anexada</div>}

                        </div>

                      </div>

                    )}



                    {/* Auto photo + notes when negative answers detected — one block per item */}

                    {q.type !== "header" && q.type !== "paragraph" && q.type !== "photo" && q.type !== "signature" && q.type !== "pass_fail" && negativeItems(q, a.value).length > 0 && (

                      <div className="mt-3 space-y-2" data-testid={`defect-panels-${q.id}`}>

                        {negativeItems(q, a.value).map((item) => {

                          const d = (a.defects || {})[item.key] || {};

                          const inputId = `defect-photo-${q.id}-${item.key}`;

                          const onDefectPhoto = (file) => {

                            if (!file) return;

                            compressImage(file).then((dataUrl) => {

                              if (dataUrl) setA(q.id, { defects: { ...(a.defects || {}), [item.key]: { ...d, photo: dataUrl } } });

                            }).catch(() => {

                              const reader = new FileReader();

                              reader.onload = () => setA(q.id, { defects: { ...(a.defects || {}), [item.key]: { ...d, photo: reader.result } } });

                              reader.readAsDataURL(file);

                            });

                          };

                          const onDefectNotes = (notes) => setA(q.id, { defects: { ...(a.defects || {}), [item.key]: { ...d, notes } } });

                          return (

                            <div key={item.key} className="p-3 rounded-md border-2 border-red-500/40 bg-red-500/5 space-y-2"

                              data-testid={`defect-panel-${q.id}-${item.key}`}>

                              <div className="text-xs uppercase tracking-widest font-semibold text-red-500 flex items-center gap-1">

                                <AlertCircle className="w-3.5 h-3.5" />

                                Anexar Foto do Defeito — {item.label}{item.response !== item.label ? ` (${item.response})` : ""}

                              </div>

                              <input type="file" accept="image/*" capture="environment"

                                className="hidden" id={inputId}

                                onChange={(e) => onDefectPhoto(e.target.files?.[0])}

                                data-testid={`defect-photo-input-${q.id}-${item.key}`} />

                              <div className="flex items-center gap-3">

                                <Button type="button" variant="outline" size="sm" disabled={readOnly}

                                  onClick={() => document.getElementById(inputId)?.click()}

                                  className="gap-2" data-testid={`defect-photo-btn-${q.id}-${item.key}`}>

                                  <Camera className="w-4 h-4" /> {d.photo ? "Trocar foto" : "Capturar foto (obrigatória)"}

                                </Button>

                                {d.photo && <img src={d.photo} alt={item.label} className="w-16 h-16 object-cover rounded-md border border-border" />}

                              </div>

                              <Textarea rows={2}

                                placeholder={`Justificativa / descrição do defeito em "${item.label}" (obrigatório)`}

                                value={d.notes || ""} disabled={readOnly}

                                onChange={(e) => onDefectNotes(e.target.value)}

                                data-testid={`defect-notes-${q.id}-${item.key}`} />

                            </div>

                          );

                        })}

                      </div>

                    )}

                  </div>

                );

              })}

            </div>

          </Card>

        ))}



        <Card className="p-4">

          <label className="text-sm font-semibold mb-2 block">Observações gerais</label>

          <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} disabled={readOnly} data-testid="general-notes" placeholder="Registre observações complementares..." />

        </Card>



        {/* Signature required at end */}

        <Card className="p-4">

          <div className="flex items-center gap-2 mb-3">

            <PenLine className="w-4 h-4 text-primary" />

            <div className="font-display font-bold uppercase tracking-tight">Assinatura do Inspetor</div>

            <Badge variant="outline" className="text-[10px] ml-auto">Obrigatória</Badge>

          </div>

          <SignaturePad value={signature} onChange={setSignature} disabled={readOnly} />

          {!signature && <div className="flex items-center gap-1 mt-2 text-xs text-amber-500"><AlertCircle className="w-3 h-3" />Assine antes de enviar</div>}

        </Card>

      </div>



      {!readOnly && (

        <div className="fixed bottom-0 left-0 right-0 lg:left-64 bg-card border-t border-border p-3 z-30">

          <div className="max-w-3xl mx-auto">

            <Button onClick={submit} disabled={submitting} className="w-full h-12 gap-2 font-display font-bold uppercase tracking-wide"

              data-testid="submit-inspection">

              <Send className="w-4 h-4" /> {submitting ? "Enviando..." : "Finalizar & Enviar Inspeção"}

            </Button>

          </div>

        </div>

      )}

    </div>

  );

}
